import { useMemo } from "react";
import { Person, PersonNameInternalEvidence } from "../../types/person";
import { initMetadata } from "../../controllers/formController";
import { SystemEntityProofreadingStatus } from "../../shared/enums/shared-enums";
import { DEFAULT_INTELLEXUS_DATE } from "../../types/intellexusDate";

export const stringifyPersonName = (
  evidence?: PersonNameInternalEvidence | null
): string => {
  if (!evidence) return "";
  const { attribute_pre = "", name = "", attribute_post = "" } = evidence;
  return [attribute_pre, name, attribute_post].filter(Boolean).join(" ");
};

const usePersonLogic = () => {
  const defaultPersonNameInternalEvidence = useMemo<PersonNameInternalEvidence>(
    () => ({
      attribute_pre: "",
      name: "",
      attribute_post: "",
      reconstruction_status_type: null,
      status_type: null,
    }),
    []
  );

  const personOriginEnum = useMemo<string>(() => "person_origin", []);
  const affiliatedTraditionsEnum = useMemo<string>(
    () => "affiliated_traditions",
    []
  );
  const personGenderEnum = useMemo<string>(() => "person_gender", []);
  const nameDesignationsStatusEnum = useMemo<string>(
    () => "name_designations_status",
    []
  );
  const nameDesignationsReconstructionStatusEnum = useMemo<string>(
    () => "name_designations_reconstruction_status",
    []
  );
  const personLinksTypeEnum = useMemo<string>(() => "person_links_type", []);
  const personLanguagesEnum = useMemo<string>(() => "person_languages", []);
  const personRolesEnum = useMemo<string>(() => "person_roles", []);
  const regionCountryAreasEnum = useMemo<string>(
    () => "region_country_areas",
    []
  );
  
  const userEnums = useMemo<string[]>(
    () => [
      personOriginEnum,
      affiliatedTraditionsEnum,
      personGenderEnum,
      nameDesignationsStatusEnum,
      nameDesignationsReconstructionStatusEnum,
      personLinksTypeEnum,
      personLanguagesEnum,
      personRolesEnum,
      regionCountryAreasEnum,
    ],
    [
      personOriginEnum,
      affiliatedTraditionsEnum,
      personGenderEnum,
      nameDesignationsStatusEnum,
      nameDesignationsReconstructionStatusEnum,
      personLinksTypeEnum,
      personLanguagesEnum,
      personRolesEnum,
      regionCountryAreasEnum,
    ]
  );

  const metadataFieldNames = useMemo<Array<keyof Person>>(
    () => [
      "name_internal_evidence",
      "primary_title_type",
      "floruit",
      "date_of_birth",
      "date_of_death",
      "affiliated_traditions",
      "region_of_activity",
      "other_designations",
      "link_to_other_databases",
    ],
    []
  );

  const defaultPerson = useMemo<Person>(() => {
    return {
      _id: "",
      _rev: "",
      _key: "",
      person_id: "",
      published_id: "",
      origin: "",
      region_of_activity: null,
      region_of_activity_id: null,
      gender: "",
      name_internal_evidence: defaultPersonNameInternalEvidence,
      other_designations: [],
      primary_title_type: "",
      link_to_other_databases: [],
      languages: [],
      date_of_birth: DEFAULT_INTELLEXUS_DATE,
      date_of_death: DEFAULT_INTELLEXUS_DATE,
      floruit: DEFAULT_INTELLEXUS_DATE,
      affiliated_traditions: [],
      place_of_birth: null,
      place_of_birth_id: null,
      notes: "",
      internal_notes: "",
      entity_status_of_proofreading:
        SystemEntityProofreadingStatus.NOT_PROOFREAD,
      attributes_metadata: initMetadata<Person>(metadataFieldNames),
    };
  }, [metadataFieldNames, defaultPersonNameInternalEvidence]);

  const getDisplayName = (person?: Person | null): string => {
    const name = stringifyPersonName(person?.name_internal_evidence);
    const displayName =
      (name || person?.published_id ? `: ` : "") +
      person?.published_id +
      (person?.published_id && name ? " - " : "") +
      name;
    return `Person${displayName}`;
  };

  return {
    userEnums,
    metadataFieldNames,
    defaultPerson,
    defaultPersonNameInternalEvidence,
    getDisplayName,
    personOriginEnum,
    affiliatedTraditionsEnum,
    personGenderEnum,
    nameDesignationsStatusEnum,
    nameDesignationsReconstructionStatusEnum,
    personLinksTypeEnum,
    personLanguagesEnum,
    personRolesEnum,
    regionCountryAreasEnum,
  };
};

export default usePersonLogic;
